import { useQuery } from "@apollo/client";
import Errors from "components/base/Errors";
import Page from "components/base/Page";
import Spinner from "components/base/Spinner";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AtRiskRevenueCard from "./components/AtRiskRevenueCard";
import DiagnosisCard from "./components/DiagnosisCard";
import EvidenceCard from "./components/EvidenceCard";
import OpportunityWatchlistCard from "./components/OpportunityWatchlistCard";
import RecoverableRevenueCard from "./components/RecoverableRevenueCard";
import RiskWatchlistCard from "./components/RiskWatchlistCard";
import SuggestedActionsCard from "./components/SuggestedActionsCard";
import TopDownDriversCard from "./components/TopDownDriversCard";
import TopUpDriversCard from "./components/TopUpDriversCard";
import TotalSalesChangeCard from "./components/TotalSalesChangeCard";
import WindowCard from "./components/WindowCard";
import { SALES_BRIEF_WINDOW_TYPE_VALUES } from "./constants";
import {
  FETCH_AI_SALES_BRIEF,
  FETCH_AI_SALES_BRIEF_BY_WINDOW_TYPE,
} from "./graphql";
import { money, toChangePct } from "./utils";

const SalesDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams({
    windowType: "YEAR_TO_DATE",
  });
  const [selectedDiagnosisId, setSelectedDiagnosisId] = useState("");

  const requestedWindowType = searchParams.get("windowType") || "";
  const defaultWindowType = SALES_BRIEF_WINDOW_TYPE_VALUES[0] || "";
  const windowType = SALES_BRIEF_WINDOW_TYPE_VALUES.includes(
    requestedWindowType,
  )
    ? requestedWindowType
    : defaultWindowType;

  const salesBriefQuery = windowType
    ? FETCH_AI_SALES_BRIEF_BY_WINDOW_TYPE[windowType]
    : FETCH_AI_SALES_BRIEF;

  const { data, loading, error } = useQuery(salesBriefQuery, {
    fetchPolicy: "cache-and-network",
  });

  const brief = data?.aiSalesBrief;

  useEffect(() => {
    if (!brief) return;

    const diagnosisIds = (brief.diagnoses || []).map(
      (diagnosis) => diagnosis.diagnosisId,
    );

    if (diagnosisIds.length === 0) {
      if (selectedDiagnosisId) setSelectedDiagnosisId("");
      return;
    }

    if (!selectedDiagnosisId || !diagnosisIds.includes(selectedDiagnosisId)) {
      setSelectedDiagnosisId(diagnosisIds[0]);
    }
  }, [brief, selectedDiagnosisId]);

  function handleWindowTypeChange(nextWindowType) {
    const nextParams = Object.fromEntries(searchParams.entries());

    if (nextWindowType) {
      nextParams.windowType = nextWindowType;
    } else {
      delete nextParams.windowType;
    }

    setSearchParams(nextParams);
  }

  if (loading) return <Spinner />;

  if (error) return <Errors error={error} />;

  const summary = brief.summary || {};
  const aiBrief = brief.aiBrief || {};
  const windowInfo = brief.window || {};

  const topDownDrivers = brief.topDownDrivers || [];
  const topUpDrivers = brief.topUpDrivers || [];
  const diagnosesSource = brief.diagnoses || [];
  const riskWatchlistSource = brief.riskWatchlist || [];
  const opportunityWatchlistSource = brief.opportunityWatchlist || [];
  const suggestedActions = aiBrief.suggestedActions || [];

  const downDrivers = topDownDrivers.map((driver, index) => ({
    rank: driver.rank ?? index + 1,
    driver: driver.entityDisplayName,
    current: Number(driver.current || 0),
    previous: Number(driver.previous || 0),
    delta: Number(driver.delta || 0),
    contribution:
      driver.contributionPct === null || driver.contributionPct === undefined
        ? "-"
        : `${Number(driver.contributionPct).toFixed(1)}%`,
  }));

  const upDrivers = topUpDrivers.map((driver, index) => ({
    rank: driver.rank ?? index + 1,
    driver: driver.entityDisplayName,
    current: Number(driver.current || 0),
    previous: Number(driver.previous || 0),
    delta: Number(driver.delta || 0),
    contribution:
      driver.contributionPct === null || driver.contributionPct === undefined
        ? "-"
        : `${Number(driver.contributionPct).toFixed(1)}%`,
  }));

  const diagnoses = diagnosesSource.map((diagnosis) => ({
    id: diagnosis.diagnosisId,
    entityDisplayName: diagnosis.entityDisplayName,
    direction: String(diagnosis.severity || "")
      .toLowerCase()
      .includes("up")
      ? "up"
      : "down",
    subject: diagnosis.title || diagnosis.entityId || "Unknown",
    confidence: diagnosis.severity || "medium",
    phenomenon: diagnosis.summary || "-",
    hypothesis: diagnosis.disproofQuestion || "-",
    evidence:
      Array.isArray(diagnosis.signals) && diagnosis.signals.length > 0
        ? diagnosis.signals
        : ["No explicit evidence returned."],
  }));

  const selectedDiagnosis =
    diagnoses.find((item) => item.id === selectedDiagnosisId) || diagnoses[0];

  const actions = suggestedActions.map((action, index) => ({
    id: `${action.entityType || "entity"}-${action.entityId || "id"}-${index}`,
    target:
      action.entityDisplayName ||
      [action.entityType, action.entityId].filter(Boolean).join(" / ") ||
      "-",
    action: action.suggestion || "-",
    impact: action.expectedImpact || "-",
    reason: action.reason || "-",
  }));

  const riskWatchlist = riskWatchlistSource.map((item) => {
    const change = toChangePct(item.currentRevenue, item.previousRevenue);

    return {
      customer: item.entityDisplayName,
      signal: item.signal || "-",
      change: `${change >= 0 ? "+" : ""}${change.toFixed(1)}%`,
      reason: `Revenue ${money(item.previousRevenue)} -> ${money(item.currentRevenue)} | Orders ${Number(item.previousOrderCount || 0)} -> ${Number(item.currentOrderCount || 0)}`,
      nextAction: item.nextBestActionHint || "-",
    };
  });

  const opportunityWatchlist = opportunityWatchlistSource.map((item) => {
    const change = toChangePct(item.currentRevenue, item.previousRevenue);
    return {
      customer: item.entityDisplayName,
      signal: item.signal || "-",
      change: `${change >= 0 ? "+" : ""}${change.toFixed(1)}%`,
      reason: "Positive signal detected by AI",
      nextAction: "Review account and push expansion follow-up",
    };
  });

  return (
    <Page
      title="Sales Dashboard - AI Review"
      subtitle="Executive view: what changed, why it changed, and what to do next."
    >
      <div className="p-6 space-y-6">
        <WindowCard
          windowType={windowType}
          onWindowTypeChange={handleWindowTypeChange}
          updatedAt={brief.generatedAt || aiBrief.generatedAt}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          <TotalSalesChangeCard totalSales={summary.totalSales} />
          <AtRiskRevenueCard value={summary.atRiskRevenue} />
          <RecoverableRevenueCard value={summary.recoverableRevenue} />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <TopDownDriversCard rows={downDrivers} />
          <TopUpDriversCard rows={upDrivers} />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <DiagnosisCard
            diagnoses={diagnoses}
            selectedDiagnosisId={selectedDiagnosis?.id || ""}
            onSelectDiagnosis={setSelectedDiagnosisId}
          />
          <EvidenceCard diagnosis={selectedDiagnosis} />
        </div>

        <SuggestedActionsCard actions={actions} />

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <RiskWatchlistCard rows={riskWatchlist} />
          <OpportunityWatchlistCard rows={opportunityWatchlist} />
        </div>

        <div className="text-xs opacity-50">
          Snapshot: {brief.snapshotId || "-"} | Status: {brief.status || "-"} |
          Window: {windowInfo.label || windowInfo.windowType || "-"} | Basis:{" "}
          {windowInfo.dateBasis || "-"}
        </div>
      </div>
    </Page>
  );
};

export default SalesDashboard;
